import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { TRoutes } from '../../shared/const'
import i18nInstance from '../../shared/i18n'
import useCountUp from '../../shared/useCountUp'

import PublicTopBar from '../../components/PublicTopBar'
import PublicFooter from '../../components/PublicFooter'

import { getDaVinciLabPage } from './content'
import logo from '../../assets/images/davincilab-logo.png'

import { BILDER } from './photos'
import styles from './style.module.scss'

/**
 * Die DaVinciLab-Seite — was hinter „Powered by DaVinciLab" steckt.
 *
 * Bis 26.08.2026 war das ein kleines Fenster in der Fußzeile. Zwei Dinge
 * sprachen dagegen, und beide hängen am Zweck: Die Seite soll einer
 * Junior-Achievement-Organisation in einem anderen Land zeigen, was hier läuft.
 *
 * 1. **Ein Fenster hat keine Adresse.** Was begeistern soll, muss man
 *    verschicken können.
 * 2. **Es geht nicht um Technik, sondern um den Alltag.** „820 Prüffälle" und
 *    „70 Auslieferungen" versteht niemand, der eine Initiative führt. Was zählt,
 *    ist die dynamische Checkliste, die Nachricht im richtigen Moment und die
 *    Zeit, die dadurch für die Menschen übrig bleibt.
 *
 * Der nüchterne Beleg dazu bleibt der Bericht unter `/plattform`; von hier
 * führt ein Verweis dorthin.
 */

/** Eine Kennzahl, die beim Hereinscrollen hochläuft. */
function Kennzahl({ value, label, note, index }) {
	const { elementRef, wert } = useCountUp(value)
	const { i18n } = useTranslation()

	return (
		/*
		 * Der Schwebe-Versatz kommt aus dem Index, damit die Kacheln nicht im
		 * Gleichschritt wippen — das sähe nach einem Fehler aus, nicht nach
		 * Leichtigkeit. Als Inline-Wert und nicht als Klasse, weil sechs
		 * Verzögerungsklassen im Stylesheet nichts erklären würden.
		 */
		<div className={styles.kennzahl} style={{ animationDelay: `${(index % 3) * 0.9}s` }}>
			<span className={styles.kennzahlWert} ref={elementRef}>
				{new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'de-DE').format(wert)}
			</span>
			<span className={styles.kennzahlLabel}>{label}</span>
			<span className={styles.kennzahlNote}>{note}</span>
		</div>
	)
}

Kennzahl.propTypes = {
	value: PropTypes.number.isRequired,
	label: PropTypes.string.isRequired,
	note: PropTypes.string.isRequired,
	index: PropTypes.number.isRequired
}

/** Eine Zeile im Balkendiagramm — Beschriftung, Balken, Zahl. */
function Balken({ label, value, max, ton }) {
	const { i18n } = useTranslation()

	return (
		<div className={styles.balkenZeile}>
			<span className={styles.balkenLabel}>{label}</span>
			<span className={styles.balkenBahn}>
				<span
					className={clsx(styles.balken, styles[ton])}
					style={{ width: `${Math.max(2, (value / max) * 100).toFixed(2)}%` }}
				/>
			</span>
			<b className={styles.balkenWert}>
				{new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'de-DE').format(value)}
			</b>
		</div>
	)
}

Balken.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.number.isRequired,
	max: PropTypes.number.isRequired,
	ton: PropTypes.string
}

Balken.defaultProps = { ton: 'tonTeal' }

/** Ein Foto aus dem Jahresbericht, mit dem Hinweis auf seine Herkunft. */
function Foto({ name, caption, className }) {
	const bild = BILDER[name]

	return (
		<figure className={clsx(styles.foto, className)}>
			<div className={styles.fotoRahmen}>
				<picture>
					<source srcSet={bild.avif} type="image/avif" />
					<img src={bild.webp} alt="" loading="lazy" />
				</picture>
				<span className={styles.fotoKi}>KI-generiert</span>
			</div>
			{caption && <figcaption className={styles.fotoText}>{caption}</figcaption>}
		</figure>
	)
}

Foto.propTypes = {
	name: PropTypes.string.isRequired,
	caption: PropTypes.string,
	className: PropTypes.string
}

/** Überschrift eines Abschnitts: der Claim groß, der Titel darunter. */
function Kopf({ claim, title }) {
	return (
		<header className={styles.abschnittKopf}>
			<p className={styles.claim}>{claim}</p>
			<h2 className={styles.abschnittTitel}>{title}</h2>
		</header>
	)
}

Kopf.propTypes = {
	claim: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired
}

export default function DaVinciLabPage() {
	const { i18n } = useTranslation()
	const c = getDaVinciLabPage(i18n.language)

	/*
	 * Die Anwendung stellt die Sprache erst im angemeldeten Bereich auf Deutsch.
	 * Diese Seite läuft ausserhalb davon — ohne das griffe die
	 * Browsererkennung. Dasselbe Muster wie auf den anderen offenen Seiten.
	 */
	useEffect(() => {
		if (i18nInstance.language !== 'ger' && i18nInstance.language !== 'en') {
			i18nInstance.changeLanguage('ger')
		}
	}, [])

	const zielRoute = {
		bilanz: TRoutes.PUBLIC_ANNUAL_REPORT,
		plattform: TRoutes.PUBLIC_PLATFORM_REPORT
	}

	const maxPhase = Math.max(...c.phases.map(p => p.value))
	const maxZiel = Math.max(...c.audiences.map(a => a.value))
	const maxAnlass = Math.max(...c.occasions.map(o => o.value))
	const maxDok = Math.max(...c.documents.map(d => d.value))

	return (
		<div className={styles.root}>
			<PublicTopBar spalte />

			{/* ------------------------------------------------ Aufmacher */}
			<header className={styles.hero}>
				<div className={styles.heroText}>
					<span className={styles.eyebrow}>{c.eyebrow}</span>
					{/*
					 * Der Titel trägt seinen Umbruch im Text (`\n`). Zwei Zeilen
					 * sind hier die Aussage — „Zeit für Jugendliche." steht für
					 * sich, und was darunter kommt, ist der Gegensatz dazu.
					 */}
					<h1 className={styles.heroTitel}>{c.title}</h1>
					<p className={styles.heroLead}>{c.lead}</p>
				</div>
				<Foto name={c.heroImage} caption={c.heroCaption} className={styles.heroFoto} />
			</header>

			{/* ------------------------------------------------ Kennzahlen */}
			<section className={styles.kennzahlenBlock}>
				{/*
				 * Der Zeitraum steht am Claim und nicht nur in der Fußnote: Sechs
				 * hochlaufende Zahlen ohne Jahr liest man als „so ist es jetzt".
				 * Es ist die Auswertung eines abgeschlossenen Schuljahrs.
				 */}
				<div className={styles.kennzahlenKopf}>
					<p className={styles.kennzahlenClaim}>{c.figuresClaim}</p>
					<span className={styles.kennzahlenZeitraum}>{c.figuresPeriod}</span>
				</div>
				<div className={styles.kennzahlen}>
					{c.figures.map((f, i) => (
						<Kennzahl key={f.label} index={i} {...f} />
					))}
				</div>
				<p className={styles.fussnote}>{c.figuresFoot}</p>
			</section>

			{/* ------------------------------------------------ Checkliste */}
			<section className={styles.abschnitt}>
				<Kopf claim={c.checklistClaim} title={c.checklistTitle} />
				<div className={styles.zweiSpalten}>
					<div>
						<p className={styles.fliesstext}>{c.checklistText}</p>
						<ol className={styles.checkliste}>
							{c.checklistItems.map((item, i) => (
								<li key={item} className={styles.checkPunkt}>
									<span className={styles.checkNr}>{String(i + 1).padStart(2, '0')}</span>
									<span>{item}</span>
								</li>
							))}
						</ol>
						<p className={styles.fussnote}>{c.checklistFoot}</p>
					</div>
					<Foto name={c.checklistImage} className={styles.seitenFoto} />
				</div>
			</section>

			{/* ------------------------------------------------ Nachrichten */}
			<section className={clsx(styles.abschnitt, styles.abschnittGetoent)}>
				<Kopf claim={c.messagesClaim} title={c.messagesTitle} />
				<div className={styles.zweiSpalten}>
					<div>
						<p className={styles.fliesstext}>{c.messagesText}</p>
						<div className={styles.miniZahlen}>
							{c.messagesStats.map(s => (
								<div key={s.label} className={styles.miniZahl}>
									<b>{s.value}</b>
									<span className={styles.miniLabel}>{s.label}</span>
									<span className={styles.miniNote}>{s.note}</span>
								</div>
							))}
						</div>
					</div>
					<Foto name={c.messagesImage} className={styles.seitenFoto} />
				</div>

				<div className={styles.zweiSpalten}>
					<div className={styles.karte}>
						<h3 className={styles.karteTitel}>{c.phasesTitle}</h3>
						{c.phases.map(p => (
							<Balken key={p.label} label={p.label} value={p.value} max={maxPhase} />
						))}
					</div>
					<div className={styles.karte}>
						<h3 className={styles.karteTitel}>{c.audiencesTitle}</h3>
						{c.audiences.map(a => (
							<Balken key={a.label} label={a.label} value={a.value} max={maxZiel} ton="tonPetrol" />
						))}
					</div>
				</div>

				<div className={styles.karte}>
					<h3 className={styles.karteTitel}>{c.occasionsTitle}</h3>
					<div className={styles.anlassRaster}>
						{c.occasions.map(o => (
							<Balken key={o.label} label={o.label} value={o.value} max={maxAnlass} ton="tonOrange" />
						))}
					</div>
					<p className={styles.fussnote}>{c.occasionsFoot}</p>
				</div>
			</section>

			{/* ------------------------------------------------ Orientierung */}
			<section className={styles.abschnitt}>
				<Kopf claim={c.orientationClaim} title={c.orientationTitle} />
				<p className={styles.fliesstext}>{c.orientationText}</p>
				<div className={styles.kacheln}>
					{c.orientationItems.map(item => (
						<div key={item.title} className={styles.kachel}>
							<h3 className={styles.kachelTitel}>{item.title}</h3>
							<p className={styles.kachelText}>{item.text}</p>
						</div>
					))}
				</div>
			</section>

			{/* ------------------------------------------------ Dokumente */}
			<section className={clsx(styles.abschnitt, styles.abschnittGetoent)}>
				<Kopf claim={c.documentsClaim} title={c.documentsTitle} />
				<div className={styles.zweiSpalten}>
					<div>
						<p className={styles.fliesstext}>{c.documentsText}</p>
						<div className={styles.karte}>
							{c.documents.map(d => (
								<Balken key={d.label} label={d.label} value={d.value} max={maxDok} />
							))}
						</div>
						<h3 className={styles.karteTitel}>{c.uploadsTitle}</h3>
						<ul className={styles.uploads}>
							{c.uploads.map(u => (
								<li key={u.label} className={styles.upload}>
									<b>{new Intl.NumberFormat('de-DE').format(u.value)}</b>
									<span>{u.label}</span>
								</li>
							))}
						</ul>
					</div>
					<Foto name={c.documentsImage} className={styles.seitenFoto} />
				</div>
			</section>

			{/* ------------------------------------------------ Rollen */}
			<section className={styles.abschnitt}>
				<Kopf claim={c.rolesClaim} title={c.rolesTitle} />
				<p className={styles.fliesstext}>{c.rolesText}</p>
				<div className={styles.rollen}>
					{c.roles.map(r => (
						<div key={r.title} className={styles.rolle}>
							<b className={styles.rolleTitel}>{r.title}</b>
							<span className={styles.rolleText}>{r.text}</span>
						</div>
					))}
				</div>
			</section>

			{/* ------------------------------------------------ Wirkung */}
			<section className={clsx(styles.abschnitt, styles.abschnittDunkel)}>
				<div className={styles.zweiSpalten}>
					<div>
						<p className={clsx(styles.claim, styles.claimHell)}>{c.effectClaim}</p>
						<h2 className={clsx(styles.abschnittTitel, styles.titelHell)}>{c.effectTitle}</h2>
						<p className={clsx(styles.fliesstext, styles.textHell)}>{c.effectText}</p>
					</div>
					<Foto name={c.effectImage} className={styles.seitenFoto} />
				</div>
			</section>

			{/* ------------------------------------------------ Abschluss */}
			<section className={styles.abschnitt}>
				<Kopf claim={c.outroClaim} title={c.outroTitle} />
				<p className={styles.fliesstext}>{c.outroText}</p>

				<div className={styles.verweise}>
					{c.outroLinks.map(link => (
						<Link key={link.to} to={zielRoute[link.to]} className={styles.verweis}>
							<b className={styles.verweisTitel}>{link.title}</b>
							<span className={styles.verweisText}>{link.text}</span>
						</Link>
					))}
				</div>

				<div className={styles.kontakt}>
					<div>
						<span className={styles.kontaktTitel}>{c.contactTitle}</span>
						{/*
						 * Der Block trägt seinen Umbruch im Text; `white-space:
						 * pre-line` macht ihn sichtbar. Zwei Felder daraus zu
						 * machen hieße, eine Anschrift aufzuteilen, die immer
						 * zusammen gepflegt wird.
						 */}
						<span className={styles.kontaktText}>{c.contact}</span>
					</div>
					{/*
					 * Das Logo ist der Verweis nach draußen — mit der Adresse
					 * darunter, damit auch auf Papier oder im Bildschirmfoto zu
					 * lesen ist, wohin er führt.
					 */}
					<a className={styles.seite} href="https://davincilab.at/" target="_blank" rel="noopener noreferrer">
						<img className={styles.logo} src={logo} alt="DaVinciLab" />
						<span>davincilab.at</span>
					</a>
				</div>

				<p className={styles.fussnote}>{c.imageNote}</p>
			</section>

			<PublicFooter spalte />
		</div>
	)
}
